import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LegalConsentCheckboxes from "./LegalConsentCheckboxes";
import { canSubmitConsent, getMissingConsents, LEGAL_VERSIONS, LegalDocument } from "@/lib/legalConsent";

const LegalConsentGate = ({ children }: { children: ReactNode }) => {
  const { user, signOut } = useAuth();
  const { t, i18n } = useTranslation();
  const [missing, setMissing] = useState<LegalDocument[]>([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    let active = true;
    (supabase as any)
      .from("user_legal_consents")
      .select("document, version")
      .eq("user_id", user.id)
      .then(({ data, error }: any) => {
        if (!active || error) return; // never block the app on a read failure
        setMissing(getMissingConsents(data ?? []));
      });
    return () => { active = false; };
  }, [user?.id]);

  const handleAccept = async () => {
    if (!user || !canSubmitConsent(acceptTerms, acceptPolicy)) return;
    setSaving(true);
    setError("");
    const rows = missing.map((document) => ({
      user_id: user.id, document, version: LEGAL_VERSIONS[document], locale: i18n.language,
    }));
    const { error } = await (supabase as any).from("user_legal_consents").insert(rows);
    setSaving(false);
    if (error && error.code !== "23505") { setError(t("legalConsent.error")); return; }
    setMissing([]);
  };

  return (
    <>
      {children}
      <Dialog open={missing.length > 0}>
        <DialogContent className="max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t("legalConsent.gateTitle")}</DialogTitle>
            <DialogDescription>{t("legalConsent.gateDescription")}</DialogDescription>
          </DialogHeader>
          <LegalConsentCheckboxes acceptTerms={acceptTerms} acceptPolicy={acceptPolicy} onTermsChange={setAcceptTerms} onPolicyChange={setAcceptPolicy} />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex flex-col gap-2">
            <Button onClick={handleAccept} disabled={saving || !canSubmitConsent(acceptTerms, acceptPolicy)}>
              {saving ? t("legalConsent.saving") : t("legalConsent.accept")}
            </Button>
            <Button variant="ghost" onClick={() => signOut()}>{t("legalConsent.signOut")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LegalConsentGate;
